import { FC } from 'react'
import {
  Container,
  Paper,
  Typography,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Box,
} from '@mui/material'

interface RopaItem {
  recurso: string
  campos: string[]
  categoria: 'Identificação' | 'Contato' | 'Sensível' | 'Comportamento'
  baseLegal: string
  finalidade: string
  retencao: string
}

const ROPA: RopaItem[] = [
  {
    recurso: 'Cliente',
    campos: ['razaoSocial', 'cnpj', 'endereco'],
    categoria: 'Identificação',
    baseLegal: 'Execução de contrato + Obrigação legal (mercado financeiro)',
    finalidade: 'Identificação da empresa cedente nas operações de crédito',
    retencao: '5 anos após o término da relação contratual',
  },
  {
    recurso: 'Cliente',
    campos: ['faturamentoAnual'],
    categoria: 'Sensível',
    baseLegal: 'Execução de contrato',
    finalidade: 'Análise de risco e estruturação de operação de crédito',
    retencao: '5 anos após o término da relação contratual',
  },
  {
    recurso: 'Contato (PF da empresa cliente)',
    campos: ['nome', 'email', 'telefone', 'cargo'],
    categoria: 'Contato',
    baseLegal: 'Execução de contrato',
    finalidade: 'Comunicação operacional e contratual com a empresa cliente',
    retencao: '2 anos após inatividade da empresa',
  },
  {
    recurso: 'Usuário do portal (Keycloak)',
    campos: ['email', 'firstName', 'lastName', 'cpf', 'cnpj'],
    categoria: 'Identificação',
    baseLegal: 'Execução de contrato + Legítimo interesse (auditoria)',
    finalidade: 'Acesso à plataforma com identificação do colaborador',
    retencao: 'Enquanto vínculo ativo; anonimização imediata sob solicitação',
  },
  {
    recurso: 'Reunião',
    campos: ['consultorNome', 'clienteNome', 'observacoes'],
    categoria: 'Identificação',
    baseLegal: 'Execução de contrato',
    finalidade: 'Registro de interações comerciais com o cliente',
    retencao: '5 anos',
  },
  {
    recurso: 'Oportunidade / Defesa de Crédito',
    campos: ['posicionamentoEstrategico', 'analiseOriginei', 'analiseFinanceira', 'recomendacao', 'dados financeiros'],
    categoria: 'Sensível',
    baseLegal: 'Execução de contrato + Obrigação legal',
    finalidade: 'Estruturação técnica da operação e defesa perante instituição financeira',
    retencao: '5 anos após encerramento da operação',
  },
  {
    recurso: 'Documentos',
    campos: ['arquivos anexados (NFs, contratos, manuais, balanços)'],
    categoria: 'Sensível',
    baseLegal: 'Execução de contrato + Obrigação legal',
    finalidade: 'Lastro documental das operações',
    retencao: '5 anos após encerramento da operação',
  },
  {
    recurso: 'Auditoria',
    campos: ['usuarioId', 'usuarioEmail (mascarado)', 'ip', 'userAgent', 'payloadAntes/Depois (mascarados)'],
    categoria: 'Comportamento',
    baseLegal: 'Legítimo interesse + Obrigação legal',
    finalidade: 'Trilha de auditoria e detecção de incidentes (Art. 46 LGPD)',
    retencao: '5 anos a partir do registro',
  },
  {
    recurso: 'Notificações',
    campos: ['destinatario', 'mensagem'],
    categoria: 'Contato',
    baseLegal: 'Execução de contrato',
    finalidade: 'Comunicação de eventos relevantes ao usuário',
    retencao: '6 meses (rotação automática)',
  },
]

const corCategoria = (c: RopaItem['categoria']): 'default' | 'info' | 'warning' | 'error' | 'success' => {
  if (c === 'Sensível') return 'error'
  if (c === 'Identificação') return 'warning'
  if (c === 'Contato') return 'info'
  return 'default'
}

const RopaPage: FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Paper sx={{ p: { xs: 3, md: 5 } }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          ROPA — Registro de Operações de Tratamento
        </Typography>
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          Art. 37 da LGPD — Lei 13.709/2018 · Última atualização: 28/04/2026
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body1" paragraph>
          Este documento lista, de forma transparente, todas as categorias de dados pessoais tratadas
          pela Plataforma Originei, suas finalidades, base legal e prazos de retenção.
        </Typography>

        <Box sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Recurso</strong></TableCell>
                <TableCell><strong>Campos</strong></TableCell>
                <TableCell><strong>Categoria</strong></TableCell>
                <TableCell><strong>Base Legal (Art. 7º)</strong></TableCell>
                <TableCell><strong>Finalidade</strong></TableCell>
                <TableCell><strong>Retenção</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ROPA.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>{item.recurso}</TableCell>
                  <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>
                    {item.campos.join(', ')}
                  </TableCell>
                  <TableCell>
                    <Chip label={item.categoria} size="small" color={corCategoria(item.categoria)} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{item.baseLegal}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{item.finalidade}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{item.retencao}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Typography variant="caption" color="text.secondary" component="div">
          Os campos marcados como <strong>sensíveis</strong> ou <strong>identificação</strong>
          recebem tratamento especial: mascaramento em logs e auditoria, controle de acesso por papel,
          trilha de auditoria com IP e User-Agent. Para exercer qualquer dos direitos previstos no
          Art. 18 LGPD, contate nosso{' '}
          <a href="/dpo" style={{ color: '#ff620c' }}>DPO</a>.
        </Typography>
      </Paper>
    </Container>
  )
}

export default RopaPage
